/**
 * UFT.Cart: the Shopify AJAX cart round-trip, normalized. Mutations resolve
 * { ok, state } (ok === false on a body carrying `status` or `errors`) and reject
 * only on a network failure; announcing and re-rendering stay with each caller.
 */

window.UFT = window.UFT || {};
window.UFT.Cart =
	window.UFT.Cart ||
	(function () {
		class Cart {
			constructor() {
				this.inFlightState = null;
			}

			shopRoot() {
				return routes.cart_url.replace(/\/cart$/, '');
			}

			isErrorBody(body) {
				return !!(body && (body.status || body.errors));
			}

			// Dropped at BOTH ends of a mutation: at the start so nothing later is handed a read that began
			// before it, and at settle so a read that raced the mutation isn't left memoized as current.
			invalidateState() {
				this.inFlightState = null;
			}

			postJSON(url, payload) {
				const body = JSON.stringify(payload);
				this.invalidateState();
				return fetch(url, { ...fetchConfig(), body })
					.then((response) => response.json())
					.then((state) => ({ ok: !this.isErrorBody(state), state }))
					.finally(() => this.invalidateState());
			}

			// Concurrent callers share one in-flight request; settling or a mutation clears it (see
			// invalidateState), so it is never stale.
			getState() {
				if (this.inFlightState) {
					return this.inFlightState;
				}
				const request = fetch(`${this.shopRoot()}/cart.js`)
					.then((response) => response.json())
					.finally(() => {
						if (this.inFlightState === request) {
							this.inFlightState = null;
						}
					});
				this.inFlightState = request;
				return request;
			}

			// cart-add-interceptor.js patches window.fetch and announces every add; callers must not announce success.
			addItems(items) {
				return this.postJSON(routes.cart_add_url, { items });
			}

			// Keyed by the line KEY, never the variant id, so line identity, quantity and selling plan survive.
			updateLine(key, changes) {
				return this.postJSON(routes.cart_change_url, { id: key, ...changes });
			}

			removeLine(key) {
				return this.updateLine(key, { quantity: 0 });
			}

			// Invalidates like postJSON even though it isn't one: it mutates the cart's discount set, so a
			// getState() already in flight describes the cart BEFORE the code applied, and handing that
			// back told a shopper their valid code was invalid.
			//
			// Rejects on a non-ok response. fetch resolves for any status, so without this a 429
			// or a 5xx was indistinguishable from a code Shopify simply did not take: the caller
			// read the cart back, found no code on it, and told the shopper their code was
			// invalid. This route answers 200 even for a typo, so a non-ok status is a real
			// transport or server failure and belongs on the caller's catch path.
			applyDiscount(code) {
				this.invalidateState();
				return fetch(`${this.shopRoot()}/discount/${encodeURIComponent(code)}`, {
					headers: { Accept: 'text/html' },
				})
					.then((response) => {
						if (!response.ok) {
							throw new Error(`Discount request failed with ${response.status}.`);
						}
						return response;
					})
					.finally(() => this.invalidateState());
			}

			// discount REPLACES the applied set rather than appending: pass survivors to drop one, an empty list to clear.
			setDiscounts(codes) {
				return this.postJSON(`${this.shopRoot()}/cart/update.js`, { discount: (codes || []).join(',') });
			}

			// A /cart/change.js rejection puts the reason in description, not errors, and errors can be a non-string object.
			errorMessage(state, fallback = 'We could not update your cart.') {
				const candidates = [state?.errors, state?.description, state?.message];
				return candidates.find((candidate) => typeof candidate === 'string' && candidate) || fallback;
			}
		}

		return new Cart();
	})();
